import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import { comics, getFeaturedComics, getPopularComics, getNewestComics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function Home() {
  const [activeTab, setActiveTab] = useState<'popular' | 'newest'>('popular');
  const [isVisible, setIsVisible] = useState(false);
  const featured = getFeaturedComics();
  const popular = getPopularComics().slice(0, 8);
  const newest = getNewestComics().slice(0, 8);
  const displayComics = activeTab === 'popular' ? popular : newest;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const siteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '51漫画 - 漫画资源大全免费全集离线阅读',
    description: '51漫画🎯下载资源页面，提供51漫画免费下载、免费版下载与APP下载，海量漫画全集免费离线阅读，支持手机客户端安装。',
    url: 'https://51manhua.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '51漫画',
      url: 'https://51manhua.com',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(siteSchema) }}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full h-[550px] md:h-[680px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20colorful%20anime%20manga%20collage%20wallpaper%2C%20popular%20manga%20characters%20in%20dynamic%20poses%2C%20speech%20bubbles%20and%20action%20lines%2C%20warm%20energetic%20color%20palette%20with%20orange%20and%20red%20accents%2C%20Japanese%20pop%20culture%20aesthetic%2C%20stylized%20illustration%20art%2C%20clean%20composition%20with%20bright%20highlights&width=1600&height=900&seq=hero-main-bg&orientation=landscape"
          alt="51漫画 - 漫画资源大全免费下载"
          title="51漫画免费下载 · 海量漫画资源"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="relative z-10 w-full text-center px-4">
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <p className="text-accent-400 text-sm md:text-base font-medium mb-3 tracking-wider">
              —— 51漫画 ——
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 mb-5 leading-tight">
              51漫画免费下载
              <br />
              <span className="text-accent-400">海量漫画</span> 随时随地畅享阅读
            </h1>
            <p className="text-background-50/80 text-sm md:text-base max-w-2xl mx-auto mb-8 leading-relaxed">
              提供<strong>51漫画免费下载</strong>、<strong>免费版下载</strong>与<strong>APP下载</strong>，
              海量漫画全集<strong>免费离线阅读</strong>，支持<strong>手机客户端安装</strong>
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#download"
                className="inline-flex items-center px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-download-2-line mr-2 text-lg"></i>
                51漫画APP下载
              </a>
              <Link
                to="/category/热血格斗"
                className="inline-flex items-center px-8 py-3.5 bg-background-50/20 backdrop-blur-sm text-background-50 rounded-full text-base font-medium border border-background-50/30 hover:bg-background-50/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line mr-2 text-lg"></i>
                开始阅读
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-background-50/60 text-2xl"></i>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { value: '15+', label: '精品漫画' },
            { value: '1800+', label: '漫画话数' },
            { value: '2.3亿+', label: '累计阅读' },
            { value: '99.7%', label: '好评率' },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl md:text-3xl font-bold text-background-50">{stat.value}</p>
              <p className="text-xs md:text-sm text-background-50/80 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Comics */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-primary-500 text-sm font-semibold tracking-wider mb-2">—— 热门推荐 ——</p>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">
              精选<strong>漫画推荐</strong>
            </h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-lg mx-auto">
              为您精选最受欢迎的漫画资源，<strong>51漫画免费下载</strong>无忧
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
            {featured.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-primary-500 text-sm font-semibold tracking-wider mb-2">—— 分类浏览 ——</p>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">
              按<strong>漫画类型</strong>探索
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              六大分类，找到属于你的那本漫画
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.name}漫画分类 - 51漫画`}
                    title={`51漫画 - ${cat.name}分类`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute bottom-3 left-4 flex items-center gap-2">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <div>
                      <h3 className="text-background-50 font-semibold text-base">{cat.name}</h3>
                      <p className="text-background-50/70 text-xs">{cat.count} 部作品</p>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-foreground-500 leading-relaxed">{cat.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Comic List with Tabs */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">
                漫画<strong>大全</strong>
              </h2>
              <p className="text-sm text-foreground-500 mt-1">
                海量<strong>漫画全集下载</strong>，随时随地<strong>离线阅读</strong>
              </p>
            </div>
            <div className="flex items-center bg-background-100 rounded-full px-1 py-1">
              <button
                onClick={() => setActiveTab('popular')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'popular'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                最受欢迎
              </button>
              <button
                onClick={() => setActiveTab('newest')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === 'newest'
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                最新更新
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
            {displayComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/热血格斗"
              className="inline-flex items-center px-6 py-2.5 border border-background-300 rounded-full text-sm font-medium text-foreground-700 hover:border-primary-400 hover:text-primary-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              查看全部漫画
              <i className="ri-arrow-right-line ml-1.5"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Download Guide */}
      <section id="download" className="py-14 md:py-20 bg-secondary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="text-accent-400 text-sm font-semibold tracking-wider mb-2">—— 下载指南 ——</p>
            <h2 className="text-2xl md:text-3xl font-heading text-background-50">
              如何<strong className="text-accent-400">免费下载</strong>51漫画？
            </h2>
            <p className="text-sm text-secondary-300 mt-2 max-w-xl mx-auto">
              三步轻松获取<strong>51漫画免费版下载</strong>，支持<strong>手机客户端安装</strong>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                icon: 'ri-smartphone-line',
                title: '下载客户端',
                desc: '选择您的设备平台，点击下载专属客户端，支持<strong>Android</strong>和<strong>iOS</strong>版本。',
              },
              {
                step: '02',
                icon: 'ri-install-line',
                title: '安装APP',
                desc: '完成<strong>手机客户端安装</strong>后，打开51漫画APP，进入海量漫画世界。',
              },
              {
                step: '03',
                icon: 'ri-download-cloud-line',
                title: '离线下载阅读',
                desc: '选择心仪漫画，一键<strong>离线下载</strong>，无需网络也能畅享<strong>漫画全集阅读</strong>。',
              },
            ].map((step, i) => (
              <div key={i} className="relative bg-secondary-800 rounded-lg p-6 text-center border border-secondary-700">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/20 text-accent-400">
                  <i className={`${step.icon} text-2xl`}></i>
                </div>
                <p className="text-accent-400 text-xs font-bold mb-1">STEP {step.step}</p>
                <h3 className="text-background-50 font-semibold text-base mb-2">{step.title}</h3>
                <p className="text-xs text-secondary-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: step.desc }}></p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#download-android"
              className="inline-flex items-center px-7 py-3 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-android-line mr-2 text-lg"></i>
              Android 下载
            </a>
            <a
              href="#download-ios"
              className="inline-flex items-center px-7 py-3 bg-background-50 text-foreground-900 rounded-full text-sm font-semibold hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-apple-line mr-2 text-lg"></i>
              iOS 下载
            </a>
            <a
              href="#download-windows"
              className="inline-flex items-center px-7 py-3 border border-secondary-600 text-background-50 rounded-full text-sm font-medium hover:border-background-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-windows-line mr-2 text-lg"></i>
              Windows 客户端
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <p className="text-primary-500 text-sm font-semibold tracking-wider mb-2">—— 常见问题 ——</p>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-950">
              关于<strong>51漫画下载</strong>的常见问题
            </h2>
          </div>
          <div className="space-y-3">
            {[
              {
                q: '51漫画免费下载是真的免费吗？',
                a: '是的！51漫画提供完全免费的漫画下载服务。您可以通过51漫画免费版下载，畅享海量漫画全集离线阅读，无需任何付费。',
              },
              {
                q: '51漫画APP支持哪些手机平台？',
                a: '51漫画APP目前支持Android和iOS两大主流手机平台。您可以在本页面找到对应版本的下载链接，完成手机客户端安装后即可使用。',
              },
              {
                q: '下载的漫画可以离线阅读吗？',
                a: '当然可以！51漫画的核心功能之一就是离线下载阅读。您可以在有网络时下载喜欢的漫画，之后无需网络连接即可随时随地畅享漫画全集阅读。',
              },
              {
                q: '51漫画的漫画资源更新频率如何？',
                a: '51漫画拥有专业的漫画资源更新团队，热门连载漫画通常会在官方发布后24小时内同步更新，确保您能第一时间阅读到最新内容。',
              },
              {
                q: '如何找到我想看的漫画类型？',
                a: '您可以通过51漫画的分类浏览功能，按照热血格斗、恋爱校园、玄幻奇幻、悬疑推理、搞笑日常、科幻未来等六大分类快速找到感兴趣的漫画类型。',
              },
            ].map((faq, i) => (
              <details key={i} className="group bg-background-100 rounded-lg border border-background-200/70">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-500 leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '51漫画免费下载是真的免费吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的！51漫画提供完全免费的漫画下载服务。您可以通过51漫画免费版下载，畅享海量漫画全集离线阅读，无需任何付费。',
                },
              },
              {
                '@type': 'Question',
                name: '51漫画APP支持哪些手机平台？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '51漫画APP目前支持Android和iOS两大主流手机平台。您可以在本页面找到对应版本的下载链接，完成手机客户端安装后即可使用。',
                },
              },
              {
                '@type': 'Question',
                name: '下载的漫画可以离线阅读吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '当然可以！51漫画的核心功能之一就是离线下载阅读。您可以在有网络时下载喜欢的漫画，之后无需网络连接即可随时随地畅享漫画全集阅读。',
                },
              },
              {
                '@type': 'Question',
                name: '51漫画的漫画资源更新频率如何？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '51漫画拥有专业的漫画资源更新团队，热门连载漫画通常会在官方发布后24小时内同步更新，确保您能第一时间阅读到最新内容。',
                },
              },
              {
                '@type': 'Question',
                name: '如何找到我想看的漫画类型？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '您可以通过51漫画的分类浏览功能，按照热血格斗、恋爱校园、玄幻奇幻、悬疑推理、搞笑日常、科幻未来等六大分类快速找到感兴趣的漫画类型。',
                },
              },
            ],
          }),
        }}
      />

      {/* CTA Section */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-4">
            准备好开始你的<strong className="text-primary-500">漫画之旅</strong>了吗？
          </h2>
          <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto">
            立即下载<strong>51漫画APP</strong>，海量<strong>漫画全集免费离线阅读</strong>，尽在掌握
          </p>
          <a
            href="#download"
            className="inline-flex items-center px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-download-cloud-2-line mr-2 text-lg"></i>
            51漫画免费下载
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}